import { useState, useEffect } from "react";
import { Upload, Newspaper, Trash2, Edit3 } from "lucide-react";

const AdminNews = () => {
  const [form, setForm] = useState({ title: "", summary: "" });
  const [image, setImage] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [newsList, setNewsList] = useState([]);

  // ✅ Fetch news list
  const fetchNews = async () => {
    try {
      const res = await fetch("http://localhost:5000/api/news");
      const data = await res.json();
      setNewsList(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFileChange = (e) => setImage(e.target.files[0]);

  // ✅ Submit new news
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("summary", form.summary);
    if (image) formData.append("image", image);

    try {
      const res = await fetch("http://localhost:5000/api/news", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        setMessage("✅ News added successfully!");
        setForm({ title: "", summary: "" });
        setImage(null);
        fetchNews();
      } else {
        setMessage(`❌ Error: ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ Server error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete news
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this news?")) return;

    try {
      const res = await fetch(`http://localhost:5000/api/news/${id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("🗑️ News deleted successfully!");
        fetchNews();
      } else {
        setMessage(`❌ Error: ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ Server error");
    }
  };

  return (
    <div className="w-full">
      {/* 🌟 Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-700 to-blue-500 text-white py-16 md:py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
            📰 University News Dashboard
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-6">
            Manage, publish, and keep the campus updated with the latest
            announcements and events.
          </p>
          <a
            href="#news-form"
            className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold shadow-md hover:bg-gray-100 transition"
          >
            + Add News
          </a>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Alert */}
        {message && (
          <div
            className={`mb-6 text-center font-medium py-3 px-4 rounded-lg shadow-md ${
              message.startsWith("✅") || message.startsWith("🗑️")
                ? "text-green-800 bg-green-100 border border-green-300"
                : "text-red-800 bg-red-100 border border-red-300"
            }`}
          >
            {message}
          </div>
        )}

        {/* Form */}
        <form
          id="news-form"
          onSubmit={handleSubmit}
          className="space-y-6 bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-200 mb-12"
          encType="multipart/form-data"
        >
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-gray-800">
            ✍️ Publish New Article
          </h2>

          <div>
            <label htmlFor="title" className="block font-semibold mb-2">
              Title
            </label>
            <input
              id="title"
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Enter news title"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              required
            />
          </div>

          <div>
            <label htmlFor="summary" className="block font-semibold mb-2">
              Summary
            </label>
            <textarea
              id="summary"
              name="summary"
              value={form.summary}
              onChange={handleChange}
              placeholder="Write a short summary..."
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              rows="5"
              required
            />
          </div>

          <div>
            <label htmlFor="image" className="block font-semibold mb-2">
              Upload Image
            </label>
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="image"
                className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition text-center"
              >
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">
                  {image ? image.name : "Click to upload or drag & drop"}
                </p>
                <p className="text-xs text-gray-400">PNG, JPG or JPEG</p>
                <input
                  id="image"
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
              </label>
            </div>

            {image && (
              <div className="mt-4">
                <p className="font-medium mb-2">Preview:</p>
                <img
                  src={URL.createObjectURL(image)}
                  alt="Preview"
                  className="w-full sm:w-72 h-40 object-cover rounded-lg shadow-md border mx-auto"
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition shadow-md ${
              loading ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Publishing..." : "Publish News"}
          </button>
        </form>

        {/* News List */}
        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-gray-800">
          📋 All News
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {newsList.map((news) => (
            <div
              key={news.id}
              className="bg-white rounded-xl shadow-md border p-4 flex flex-col hover:shadow-lg transition"
            >
              <img
                src={news.image_url || "https://via.placeholder.com/300x200"}
                alt={news.title}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-lg font-bold mb-2 text-gray-900">
                {news.title}
              </h3>
              <p className="text-gray-600 mb-4 line-clamp-3">{news.summary}</p>
              <div className="flex justify-between gap-3 mt-auto">
                <button
                  onClick={() => handleDelete(news.id)}
                  className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                >
                  <Trash2 size={16} /> Delete
                </button>
                <button
                  onClick={() => alert("Edit feature coming soon!")}
                  className="flex items-center gap-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition text-sm"
                >
                  <Edit3 size={16} /> Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminNews;
